'use client'

import { CircleCheck, CircleX, CircleMinus } from 'lucide-react'

interface ProviderResult {
  provider: string
  available: boolean
  queries: {
    query: string
    queryType: 'brand' | 'competitive' | 'reputation'
    brandMentioned: boolean
    urlCited: boolean
    sentiment: 'positive' | 'neutral' | 'negative' | 'not_mentioned'
    prominence: 'primary' | 'mentioned' | 'listed' | 'absent'
    snippet: string
  }[]
  expectedQueryCount?: number
  usableQueryCount?: number
  failedQueryCount?: number
  emptyQueryCount?: number
  overallMentionRate: number
  urlCitationRate: number
  reportOnly?: boolean
  error?: string
}

const PROVIDER_COLORS: Record<string, string> = {
  ChatGPT: '#10a37f',
  Perplexity: '#20808d',
  Claude: '#cc9b7a',
  Gemini: '#4285f4',
  Grok: 'var(--foreground)',
}

interface ProviderCardProps {
  provider: ProviderResult
}

export default function ProviderCard({ provider }: ProviderCardProps) {
  const color = PROVIDER_COLORS[provider.provider] || '#008dcc'
  const colorTint = `color-mix(in srgb, ${color} 8%, transparent)`
  const colorMuted = `color-mix(in srgb, ${color} 31%, transparent)`
  const mentionRate = Math.round(provider.overallMentionRate * 100)
  const mentioned = provider.queries.filter(q => q.brandMentioned).length
  const hasExplicitResponseCounts =
    typeof provider.usableQueryCount === 'number' &&
    typeof provider.expectedQueryCount === 'number'
  const usableResponses = provider.usableQueryCount ?? provider.queries.length
  const expectedResponses =
    provider.expectedQueryCount ?? provider.queries.length

  if (!provider.available) {
    return (
      <div className='bg-background border border-border/60 rounded-xl p-4 opacity-60'>
        <div className='flex items-center gap-2 mb-2'>
          <span
            className='h-2.5 w-2.5 rounded-full'
            style={{ backgroundColor: colorMuted }}
          />
          <span className='text-sm font-semibold text-foreground/60'>
            {provider.provider}
          </span>
          {provider.reportOnly && (
            <span className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>
              Report only
            </span>
          )}
        </div>
        <p className='text-xs text-muted-foreground'>
          {hasExplicitResponseCounts
            ? `${usableResponses}/${expectedResponses} responses usable`
            : 'Not available for this scan'}
        </p>
      </div>
    )
  }

  return (
    <div className='bg-background border border-border/60 rounded-xl p-4'>
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2'>
          <span
            className='h-2.5 w-2.5 rounded-full'
            style={{ backgroundColor: color }}
          />
          <span className='text-sm font-semibold text-foreground'>
            {provider.provider}
          </span>
          {provider.reportOnly && (
            <span className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>
              Report only
            </span>
          )}
        </div>
        <span
          className='text-xs font-bold rounded-full px-2.5 py-0.5'
          style={{ backgroundColor: colorTint, color }}
        >
          {mentionRate}%
        </span>
      </div>

      {/* Mention bar */}
      <div className='h-1.5 rounded-full bg-muted overflow-hidden mb-3'>
        <div
          className='h-full rounded-full transition-all duration-700 ease-out'
          style={{ width: `${mentionRate}%`, backgroundColor: color }}
        />
      </div>

      <div className='flex items-center justify-between text-xs text-muted-foreground mb-3'>
        <span>
          {usableResponses}/{expectedResponses} responses usable
        </span>
        {provider.provider === 'Perplexity' && provider.urlCitationRate > 0 && (
          <span style={{ color }}>
            {Math.round(provider.urlCitationRate * 100)}% URL cited
          </span>
        )}
      </div>

      <p className='text-xs text-muted-foreground mb-3'>
        {mentioned}/{usableResponses} usable responses mention your brand
      </p>

      {/* Per-query results */}
      <div className='space-y-1.5'>
        {provider.queries.map((q, i) => (
          <div
            key={i}
            className='flex items-start gap-2 text-xs text-foreground/70'
          >
            {q.brandMentioned ? (
              <CircleCheck
                size={14}
                className='mt-0.5 shrink-0'
                style={{ color: '#22c55e' }}
              />
            ) : (
              <CircleX
                size={14}
                className='mt-0.5 shrink-0'
                style={{ color: '#F90B8A' }}
              />
            )}
            <div className='flex-1 min-w-0'>
              <span className='text-foreground/80'>{q.query}</span>
              {q.brandMentioned && (
                <div className='flex items-center gap-1.5 mt-0.5'>
                  <span
                    className='text-[10px] font-medium rounded-full px-1.5 py-0'
                    style={{
                      backgroundColor:
                        q.prominence === 'primary'
                          ? '#22c55e15'
                          : q.prominence === 'mentioned'
                            ? colorTint
                            : '#FDC30415',
                      color:
                        q.prominence === 'primary'
                          ? '#22c55e'
                          : q.prominence === 'mentioned'
                            ? color
                            : '#FDC304',
                    }}
                  >
                    {q.prominence}
                  </span>
                  {q.sentiment !== 'not_mentioned' && (
                    <span className='text-[10px] text-muted-foreground'>
                      {q.sentiment === 'positive' ? (
                        <CircleCheck
                          size={10}
                          className='inline'
                          style={{ color: '#22c55e' }}
                        />
                      ) : q.sentiment === 'negative' ? (
                        <CircleX
                          size={10}
                          className='inline'
                          style={{ color: '#F90B8A' }}
                        />
                      ) : (
                        <CircleMinus
                          size={10}
                          className='inline'
                          style={{ color: '#FDC304' }}
                        />
                      )}
                    </span>
                  )}
                  {q.urlCited && (
                    <span
                      className='text-[10px] font-medium rounded-full px-1.5 py-0'
                      style={{ backgroundColor: colorTint, color }}
                    >
                      URL cited
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
